import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Alert } from "react-bootstrap";
import heroBg from "../assets/images/HeroBanner.png";
import { useNavigate, useLocation } from "react-router-dom";
import FacotPaymentUtils from "../utils/facotPaymentUtils";
import "../styles/FacotPaymentSuccess.css";

const FacotPaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [applicationData, setApplicationData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Get application data from location state or fetch from API
    if (location.state?.applicationData) {
      setApplicationData(location.state.applicationData);
      fetchPaymentDetails(location.state.applicationData._id);
    } else {
      setError("No application data found.");
      setLoading(false);
    }
  }, [location.state]);

  const fetchPaymentDetails = async (applicationId) => {
    const result = await FacotPaymentUtils.getPaymentDetails(applicationId);
    if (result.success) {
      setPaymentData(result.data);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const getPaymentStatusText = (status) => FacotPaymentUtils.getPaymentStatusText(status);
  const getApprovalStatusText = (status) => FacotPaymentUtils.getApprovalStatusText(status);
  const getStatusVariant = (status) => FacotPaymentUtils.getStatusVariant(status);
  const isProcessComplete = () => FacotPaymentUtils.isProcessComplete(paymentData?.application);
  const canMakeSecondPayment = () => FacotPaymentUtils.canMakeSecondPayment(paymentData?.application);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !applicationData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <Alert variant="danger">{error || "No application data found."}</Alert>
      </div>
    );
  }

  return (
    <>
      {/* Fixed Hero Section */}
      <section
        className="hero-fixed"
        style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="hero-scroll-content">
          <div className="hero-content-home poppins-regular">
            <h1 className="hero-heading">All India Occupational Therapists' Association</h1>
          </div>
        </div>
      </section>

      {/* Scrollable Content Container */}
      <div className="scrollable-content-home">
        <div className="content-container facot-success-content-container">
          <section className="facot-success-section">
            <Container>
              <h2 className="text-center facot-success-heading mb-4">
                FACOT Application Status
              </h2>

              {/* Success Message */}
              <Card className="mb-4 shadow-sm success-card">
                <Card.Body className="text-center">
                  <div className="success-icon mb-3">
                    <i className="fas fa-check-circle fa-4x text-success"></i>
                  </div>
                  <h3 className="text-success mb-3">Application Submitted Successfully!</h3>
                  <p className="lead">
                    Your FACOT application has been received and is being processed.
                  </p>
                </Card.Body>
              </Card>

              {/* Application Details */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="card-header-facot">
                  <h4 className="mb-0">Application Details</h4>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Applicant Name:</strong> {applicationData.applicantName}</p>
                      <p><strong>Email:</strong> {applicationData.email}</p>
                      <p><strong>AIOTA Membership No:</strong> {applicationData.aiotaMembershipNo}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>ACOT Registration No:</strong> {applicationData.acotRegistrationNo}</p>
                      <p><strong>Application Date:</strong> {new Date(applicationData.createdAt).toLocaleDateString()}</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Payment Status */}
              {paymentData?.application && (
                <Card className="mb-4 shadow-sm">
                  <Card.Header>
                    <h4 className="mb-0">Payment Status</h4>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <div className="payment-status-item">
                          <h6>First Payment (₹3,540)</h6>
                          <div className="status-badges">
                            <Alert variant={getStatusVariant(paymentData.application.paymentfirst_status)} className="d-inline-block me-2">
                              Payment: {getPaymentStatusText(paymentData.application.paymentfirst_status)}
                            </Alert>
                            <Alert variant={getStatusVariant(paymentData.application.firstapproval_status)} className="d-inline-block">
                              Approval: {getApprovalStatusText(paymentData.application.firstapproval_status)}
                            </Alert>
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="payment-status-item">
                          <h6>Second Payment (₹8,260)</h6>
                          <div className="status-badges">
                            <Alert variant={getStatusVariant(paymentData.application.paymentafter_status)} className="d-inline-block me-2">
                              Payment: {getPaymentStatusText(paymentData.application.paymentafter_status)}
                            </Alert>
                            <Alert variant={getStatusVariant(paymentData.application.afterapproval_status)} className="d-inline-block">
                              Approval: {getApprovalStatusText(paymentData.application.afterapproval_status)}
                            </Alert>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* Process Complete Message */}
              {isProcessComplete() && (
                <Card className="mb-4 shadow-sm border-success">
                  <Card.Body className="text-center">
                    <div className="success-icon mb-3">
                      <i className="fas fa-certificate fa-3x text-success"></i>
                    </div>
                    <h4 className="text-success mb-3">Congratulations!</h4>
                    <p className="lead">
                      Your FACOT application has been fully processed and approved. 
                      Your FACOT certificate has been generated and sent to your email address.
                    </p>
                    <Alert variant="info" className="mt-3">
                      <i className="fas fa-envelope me-2"></i>
                      Please check your email for the FACOT certificate. If you don't receive it within 24 hours, 
                      please contact the AIOTA office.
                    </Alert>
                  </Card.Body>
                </Card>
              )}

              {/* Next Steps */}
              <Card className="mb-4 shadow-sm">
                <Card.Header>
                  <h4 className="mb-0">Next Steps</h4>
                </Card.Header>
                <Card.Body>
                  {isProcessComplete() ? (
                    <div className="next-steps-complete">
                      <h5 className="text-success">Process Complete!</h5>
                      <ul className="list-unstyled">
                        <li><i className="fas fa-check text-success me-2"></i>Application submitted</li>
                        <li><i className="fas fa-check text-success me-2"></i>First payment completed and approved</li>
                        <li><i className="fas fa-check text-success me-2"></i>Second payment completed and approved</li>
                        <li><i className="fas fa-check text-success me-2"></i>FACOT certificate generated and sent</li>
                      </ul>
                    </div>
                  ) : canMakeSecondPayment() ? (
                    <div className="next-steps-pending">
                      <h5 className="text-warning">Ready for Second Payment</h5>
                      <p>Your first payment has been completed and approved. You can now proceed with the second payment.</p>
                      <Button 
                        variant="primary" 
                        size="lg"
                        onClick={() => navigate('/facot-payment', { state: { applicationData } })}
                      >
                        Proceed to Second Payment
                      </Button>
                    </div>
                  ) : (
                    <div className="next-steps-pending">
                      <h5 className="text-info">Processing in Progress</h5>
                      <ul className="list-unstyled">
                        <li><i className="fas fa-clock text-info me-2"></i>First payment is being processed</li>
                        <li><i className="fas fa-clock text-info me-2"></i>Awaiting secretary approval</li>
                        <li><i className="fas fa-clock text-info me-2"></i>Second payment will be available after approval</li>
                        <li><i className="fas fa-clock text-info me-2"></i>Certificate will be generated after final approval</li>
                      </ul>
                      <p className="text-muted">
                        You will receive email notifications at each step of the process.
                      </p>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Payment History */}
              {paymentData?.payments && paymentData.payments.length > 0 && (
                <Card className="mb-4 shadow-sm">
                  <Card.Header>
                    <h4 className="mb-0">Payment History</h4>
                  </Card.Header>
                  <Card.Body>
                    <div className="payment-history">
                      {paymentData.payments.map((payment, index) => (
                        <div key={index} className="payment-item">
                          <div className="payment-info">
                            <h6>Payment #{index + 1}</h6>
                            <p className="mb-1">
                              <strong>Amount:</strong> ₹{payment.amount ? (payment.amount / 100).toLocaleString() : 'N/A'}
                            </p>
                            <p className="mb-1">
                              <strong>Status:</strong> 
                              <span className={`badge ms-2 ${payment.status === 'completed' ? 'bg-success' : 'bg-warning'}`}>
                                {payment.status}
                              </span>
                            </p>
                            <p className="mb-0">
                              <strong>Date:</strong> {new Date(payment.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* Contact Information */}
              <Card className="mb-4 shadow-sm">
                <Card.Header>
                  <h4 className="mb-0">Contact Information</h4>
                </Card.Header>
                <Card.Body>
                  <p>For any questions or concerns regarding your FACOT application, please contact:</p>
                  <div className="contact-info">
                    <p><strong>AIOTA Office:</strong></p>
                    <p>Email: info@aiota.org</p>
                    <p>Phone: +91-XXXXXXXXXX</p>
                    <p>Address: [AIOTA Office Address]</p>
                  </div>
                </Card.Body>
              </Card>

              {/* Action Buttons */}
              <Row className="d-flex justify-content-center mb-4">
                <Col md={8} className="text-center">
                  <Button
                    variant="outline-primary"
                    onClick={() => navigate('/facot-payment', { state: { applicationData } })}
                    className="me-3"
                  >
                    View Payment Status
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/facot-application-form')}
                    className="me-3"
                  >
                    Submit New Application
                  </Button>
                  <Button
                    variant="outline-success"
                    onClick={() => navigate('/')}
                  >
                    Back to Home
                  </Button>
                </Col>
              </Row>
            </Container>
          </section>
        </div>
      </div>
    </>
  );
};

export default FacotPaymentSuccess;
